// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

// Orchestrates automatic price sync (see lib/priceSync.ts). Two entry points:
//  - run(): manual "Update prices now" — always collects a fresh plan.
//  - the auto effect: fires once per unlock when the (encrypted, synced)
//    `autoPriceSync` setting is on. The plan's built-in "already priced today"
//    dedup makes this idempotent: a second unlock the same day collects an
//    empty plan and makes no network requests at all.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useApp } from '../store/AppContext.js';
import { useEncryptAndPushBatch } from './useEncryptAndPush.js';
import { buildEvent } from '../lib/eventLog.js';
import {
  collectPriceSyncPlan,
  runPriceSync,
  planIsEmpty,
} from '../lib/priceSync.js';
import type { LedgerEvent } from '../types.js';

export interface PriceSyncStatus {
  running: boolean;
  /** Number of prices written by the last run (null = never ran). */
  updated: number | null;
  /** commodityIds that could not be priced in the last run. */
  unresolved: string[];
  /** Equity commodityIds skipped because no Alpha Vantage key is configured. */
  needsApiKey: string[];
  error: string | null;
}

const IDLE: PriceSyncStatus = {
  running: false,
  updated: null,
  unresolved: [],
  needsApiKey: [],
  error: null,
};

export function usePriceSync(
  worker: Worker | null,
  opts: { auto?: boolean } = {},
): {
  status: PriceSyncStatus;
  run: () => Promise<void>;
} {
  const { auto = false } = opts;
  const { state } = useApp();
  const { auth, ledger, appSettings } = state;
  const encryptAndPushBatch = useEncryptAndPushBatch(worker, auth.keyData);
  const [status, setStatus] = useState<PriceSyncStatus>(IDLE);

  // Refs so the auto-run effect doesn't retrigger on every ledger event.
  const stateRef = useRef(state);
  stateRef.current = state;

  const run = useCallback(async (): Promise<void> => {
    const { auth: a, ledger: l, appSettings: s } = stateRef.current;
    if (!a.token || !a.syncUrl) return;
    const homeCurrency = s.homeCurrency || 'USD';
    const today = new Date().toISOString().slice(0, 10);
    const plan = collectPriceSyncPlan(l, homeCurrency, today);
    if (planIsEmpty(plan)) {
      setStatus({ running: false, updated: 0, unresolved: [], needsApiKey: [], error: null });
      return;
    }
    setStatus((prev) => ({ ...prev, running: true, error: null }));
    try {
      const result = await runPriceSync(plan, {
        baseUrl: a.syncUrl,
        token: a.token,
        homeCurrency,
        today,
        apiKey: s.alphaVantageKey,
      });
      if (result.events.length > 0) {
        const events: LedgerEvent[] = [];
        let seq = stateRef.current.ledger.lastSequence;
        for (const payload of result.events) {
          events.push(await buildEvent('PriceAdded', payload, ++seq));
        }
        await encryptAndPushBatch(events);
      }
      setStatus({
        running: false,
        updated: result.events.length,
        unresolved: result.unresolved,
        needsApiKey: result.needsApiKey,
        error: null,
      });
    } catch (err) {
      setStatus({
        running: false,
        updated: null,
        unresolved: [],
        needsApiKey: [],
        error: err instanceof Error ? err.message : 'sync failed',
      });
    }
  }, [encryptAndPushBatch]);

  // Auto-run once per unlock when enabled (only the MainApp-mounted instance
  // passes auto: true). Waits for the ledger to be loaded (lastSequence set)
  // so the plan sees real holdings, not an empty ledger. Idempotent: on a day
  // whose prices already exist, the plan is empty and no request is made.
  const autoRanRef = useRef(false);
  const ledgerLoaded = ledger.lastSequence > 0;
  useEffect(() => {
    if (!auto || autoRanRef.current) return;
    if (!appSettings.autoPriceSync) return;
    if (!auth.isAuthenticated || !auth.token || !ledgerLoaded) return;
    autoRanRef.current = true;
    void run();
  }, [auto, appSettings.autoPriceSync, auth.isAuthenticated, auth.token, ledgerLoaded, run]);

  return { status, run };
}
